import { useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import { Button } from '@/components/ui/button'

interface PairingCodeDisplayProps {
  code: string
  expiresAt: string
}

export function PairingCodeDisplay({
  code,
  expiresAt,
}: PairingCodeDisplayProps) {
  const { t: tEdges } = useTranslation('edges')
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      toast.success(tEdges('pairing.copySuccess'))
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      toast.error(tEdges('pairing.copyFailed'))
    }
  }

  const expiryDate = new Date(expiresAt)
  const formattedExpiry = expiryDate.toLocaleString('de-AT', {
    dateStyle: 'short',
    timeStyle: 'short',
  })

  return (
    <div className="space-y-4">
      <div className="bg-muted p-6 rounded-lg text-center">
        <p className="text-sm text-muted-foreground mb-2">
          {tEdges('pairing.title')}
        </p>
        <p className="text-3xl font-mono font-bold tracking-wider">{code}</p>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          <p>
            {tEdges('pairing.expiresLabel')} {formattedExpiry}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleCopy}>
          {copied ? (
            <Check className="w-4 h-4 mr-2" />
          ) : (
            <Copy className="w-4 h-4 mr-2" />
          )}
          {copied ? tEdges('pairing.copied') : tEdges('pairing.copyCode')}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        {tEdges('pairing.helper')}
      </p>
    </div>
  )
}
